'use client';
import React, { useEffect, useState } from 'react';
 import Icon from'@/components/ui/AppIcon';

interface PriceData {
  karat: string
  purity: string
  price: number
  color: string
}

const LivePricingSection: React.FC = () => {
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [mainPrice, setMainPrice] = useState<number>(2043.75)
  const [priceChange, setPriceChange] = useState<number>(12.35)
  const [priceChangePercent, setPriceChangePercent] = useState<number>(0.61)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const priceData: PriceData[] = [
    { karat: '24K', purity: '99.9%', price: mainPrice, color: '#FFD700' },
    { karat: '22K', purity: '91.6%', price: mainPrice * 0.916, color: '#EEC900' },
    { karat: '18K', purity: '75.0%', price: mainPrice * 0.75, color: '#DAA520' },
  ]

  // Mock price history for sparkline (last 7 days)
  const priceHistory = [
    2031.4, 2028.9, 2035.2, 2041.6, 2038.3, 2042.1, mainPrice,
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setIsLoading(true)
      setTimeout(() => {
        const randomChange = (Math.random() - 0.5) * 5
        const newPrice = mainPrice + randomChange
        const newChange = newPrice - 2031.4
        const newChangePercent = (newChange / 2031.4) * 100

        setMainPrice(parseFloat(newPrice.toFixed(2)))
        setPriceChange(parseFloat(newChange.toFixed(2)))
        setPriceChangePercent(parseFloat(newChangePercent.toFixed(2)))
        setLastUpdated(new Date())
        setIsLoading(false)
      }, 500)
    }, 15000)

    return () => clearInterval(interval)
  }, [mainPrice])

  const maxPrice = Math.max(...priceHistory)
  const minPrice = Math.min(...priceHistory)

  return (
    <section id="pricing-section" className="py-24 md:py-32 px-6 md:px-12 bg-background relative">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
            Live Market Prices
          </span>
          <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground mb-4">
            Real-Time Gold Pricing
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Transparent pricing updated every 15 minutes. No hidden fees.
          </p>
        </div>

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Large Main Price Card with Enhanced Hover */}
          <div className="lg:col-span-7 glass-card p-8 md:p-10 rounded-3xl border border-border shadow-2xl relative overflow-hidden reveal hover-lift transition-smooth">
            {/* Shimmer Effect */}
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary/10 to-transparent pointer-events-none animate-shimmer-effect"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-sm font-body font-medium uppercase tracking-wider text-muted-foreground mb-2 block">
                    Gold Spot Price (24K)
                  </span>
                  <div className="flex items-baseline gap-3">
                    <span className={`text-6xl md:text-7xl font-display font-bold text-foreground transition-smooth ${isLoading ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
                      ${mainPrice.toLocaleString()}
                    </span>
                    <span className="text-2xl font-body text-muted-foreground">/oz</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 justify-end mb-2">
                    <Icon
                      name={priceChange >= 0 ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'}
                      size={24}
                      className={priceChange >= 0 ? 'text-success' : 'text-error'}
                    />
                    <span
                      className={`text-2xl font-display font-bold ${
                        priceChange >= 0 ? 'text-success' : 'text-error'
                      }`}
                    >
                      {priceChange >= 0 ? '+' : ''}
                      {priceChange.toFixed(2)}
                    </span>
                  </div>
                  <span
                    className={`text-lg font-body ${
                      priceChange >= 0 ? 'text-success' : 'text-error'
                    }`}
                  >
                    {priceChangePercent >= 0 ? '+' : ''}
                    {priceChangePercent.toFixed(2)}%
                  </span>
                </div>
              </div>

              {/* 7-Day Sparkline Chart with Enhanced Hover */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-xs font-body text-muted-foreground mb-2">
                  <span>Last 7 Days</span>
                  <span>Price Movement</span>
                </div>
                <div className="w-full h-20 flex items-end gap-1">
                  {priceHistory.map((price, index) => {
                    const heightPercent = ((price - minPrice) / (maxPrice - minPrice)) * 100
                    return (
                      <div
                        key={`sparkline-${index}`}
                        className="flex-1 bg-primary/20 rounded-t-sm transition-smooth hover:bg-primary/60 hover:scale-110 cursor-pointer"
                        style={{ height: `${heightPercent}%` }}
                        title={`Day ${index + 1}: $${price.toFixed(2)}`}
                      ></div>
                    )
                  })}
                </div>
              </div>

              {/* Last Updated */}
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
                  <span className="text-sm font-body text-muted-foreground">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                </div>
                <span className="text-xs font-body text-muted-foreground italic">
                  Prices updated every 15 minutes
                </span>
              </div>
            </div>
          </div>

          {/* Right Column: 3 Mini Price Cards with Enhanced Animations */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {priceData.map((data, index) => (
              <div
                key={data.karat}
                className="glass-card p-6 rounded-2xl border border-border shadow-lg hover:shadow-2xl transition-smooth hover:-translate-y-2 hover-glow relative overflow-hidden reveal"
                style={{ animationDelay: `${0.1 + index * 0.1}s` }}
              >
                {/* Shimmer Effect */}
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary/10 to-transparent pointer-events-none animate-shimmer-effect"></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-2xl font-display font-bold text-foreground">
                        {data.karat}
                      </span>
                      <span className="text-sm font-body text-muted-foreground ml-2">
                        {data.purity}
                      </span>
                    </div>
                    <div
                      className="w-8 h-8 rounded-full transition-smooth hover:scale-125"
                      style={{ backgroundColor: data.color }}
                    ></div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-display font-bold text-foreground">
                      ${data.price.toFixed(2)}
                    </span>
                    <span className="text-sm font-body text-muted-foreground">/oz</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center reveal">
          <p className="text-sm font-body text-muted-foreground italic">
            Prices are indicative and subject to change. Final pricing confirmed upon order placement.
          </p>
        </div>
      </div>
    </section>
  )
}

export default LivePricingSection